import { useEffect, useRef, useState } from "react";

export default function AuthorSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-white overflow-hidden relative">
      <div
        ref={ref}
        className={`w-full transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          <div className="flex-1 w-full max-w-md lg:max-w-none">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5] shadow-lg bg-background-100">
              <img
                alt="Kevin Nagel - Children's Author"
                className="w-full h-full object-cover"
                src="../../../../images/ca761f2324ab95e2969b180eba36caf4.png"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="inline-block px-3 py-1 bg-white/90 backdrop-blur-sm text-foreground-950 text-xs font-semibold rounded-full">
                  <i className="ri-pencil-line mr-1" />
                  Author &amp; Storyteller
                </span>
              </div>
            </div>
          </div>

          <div className="flex-1 text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 bg-accent-100 text-accent-700 text-sm font-semibold rounded-full mb-4">
              Meet the Storyteller
            </span>
            <h2 className="font-bold text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-4">
              Kevin Nagel
            </h2>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed mb-4">
              Kevin Nagel is the creator of the Cappy and Barry Adventures series, where
              playful stories inspire meaningful life lessons. As an author, educator,
              consultant and entrepreneur, Kevin believes some of life&apos;s important
              lessons are best learned through simple stories and shared moments.
            </p>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed mb-4">
              With more than 20 years of teaching, including (ESL), and computer and
              business consulting experience, Kevin has spent much of his career helping
              people learn, grow, and overcome challenges, both locally and abroad. He
              continues to work occasionally as a substitute teacher, giving him ongoing
              opportunities to inspire and connect with young learners.
            </p>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed mb-6">
              Inspired by the gentle, friendly nature of capybaras, Kevin created Cappy
              and Barry to help children develop character through stories about kindness,
              honesty, responsibility, patience, courage, and empathy through reading
              about engaging adventures. Every story is written to entertain young readers
              while encouraging meaningful conversations that continue long after the
              final page is turned.
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-sm md:text-base text-foreground-700">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0" />
                Growing Book Series + Art Projects
              </li>
              <li className="flex items-center gap-2 text-sm md:text-base text-foreground-700">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0" />
                Ages 2&ndash;7
              </li>
              <li className="flex items-center gap-2 text-sm md:text-base text-foreground-700">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0" />
                Character-Building Adventures
              </li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <div className="flex items-center gap-2 bg-background-100 px-4 py-2.5 rounded-full">
                <i className="ri-book-3-line text-primary-500" />
                <span className="text-sm text-foreground-800 font-medium">
                  5 Books
                </span>
              </div>
              <div className="flex items-center gap-2 bg-background-100 px-4 py-2.5 rounded-full">
                <i className="ri-user-heart-line text-primary-500" />
                <span className="text-sm text-foreground-800 font-medium">
                  Ages 2&ndash;7
                </span>
              </div>
              <div className="flex items-center gap-2 bg-background-100 px-4 py-2.5 rounded-full">
                <i className="ri-lightbulb-line text-primary-500" />
                <span className="text-sm text-foreground-800 font-medium">
                  5 Life Lessons
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}